import { z } from 'zod';
import { CRM_MODULES, CUSTOM_FIELD_TYPES, type CrmModule, type CustomFieldType } from '../enums.js';
import { optionalNumber, optionalText } from './common.js';

/**
 * Custom fields
 * =============
 * Admins define extra fields per module. Values live in each record's
 * `customFields` JSONB; this metadata supplies the type, the validation and
 * the form control — so one definition drives the editor, the API's validation
 * and the record view.
 */

/** Reserved because they would collide with real columns or with our internals. */
const RESERVED_API_NAMES = new Set([
  'id',
  'organizationId',
  'businessManagerId',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'customFields',
]);

export const customFieldApiName = z
  .string()
  .trim()
  .min(2)
  .max(48)
  // A JSONB key that is also a valid identifier keeps `customFields.territory`
  // addressable from workflow conditions and merge tokens without quoting.
  .regex(/^[a-z][a-zA-Z0-9]*$/u, 'Use letters and numbers, starting with a lowercase letter')
  .refine((value) => !RESERVED_API_NAMES.has(value), 'That name is reserved');

export const picklistOptionSchema = z.object({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(80),
  color: z.string().max(9).optional(),
});

/**
 * What a PDF custom field's value looks like in a record's `customFields`
 * JSONB — a pointer to the uploaded file, not the bytes themselves. The
 * bytes live in `CustomFieldFile`, addressed by `fileId`; the rest is copied
 * alongside so a list or detail view can show a filename and size without a
 * second round trip.
 */
export const customFieldFileRefSchema = z.object({
  fileId: z.string().uuid(),
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().min(0),
});
export type CustomFieldFileRef = z.infer<typeof customFieldFileRefSchema>;

/**
 * The shape of a field, before the cross-field rules.
 *
 * Exported so a custom module can build its own variant — its fields belong to
 * the module in the URL rather than to one named in the body, and they can be
 * lookups. Zod refuses `.omit()` on a schema that already carries refinements,
 * so the object has to be shared rather than the finished schema carved up.
 */
export const customFieldShape = {
  apiName: customFieldApiName,
  label: z.string().trim().min(1, 'Required').max(80),
  type: z.enum(CUSTOM_FIELD_TYPES),
  helpText: optionalText(200),
  isRequired: z.coerce.boolean().default(false),
  options: z.array(picklistOptionSchema).max(100).default([]),
  defaultValue: z.unknown().optional(),
  minValue: optionalNumber(z.coerce.number()),
  maxValue: optionalNumber(z.coerce.number()),
  maxLength: optionalNumber(z.coerce.number().int().min(1).max(20_000)),
  position: z.coerce.number().int().min(0).max(200).default(0),
  isActive: z.coerce.boolean().default(true),
};

/** The rules that span more than one field, applied to any variant of it. */
export function withFieldRules<T extends z.ZodObject>(schema: T) {
  return schema
    .refine(
      (value) =>
        !['PICKLIST', 'MULTI_PICKLIST'].includes(String(value.type)) ||
        (Array.isArray(value.options) && value.options.length > 0),
      // A picklist with no options is a control nobody can use.
      { message: 'Add at least one option', path: ['options'] },
    )
    .refine(
      // Null as well as undefined: a blank number input now arrives as null.
      (value) =>
        value.minValue == null ||
        value.maxValue == null ||
        Number(value.minValue) <= Number(value.maxValue),
      { message: 'Minimum must not exceed maximum', path: ['maxValue'] },
    );
}

export const createCustomFieldSchema = z
  .object({
    module: z.enum(CRM_MODULES),
    apiName: customFieldApiName,
    label: z.string().trim().min(1, 'Required').max(80),
    type: z.enum(CUSTOM_FIELD_TYPES),
    helpText: optionalText(200),
    isRequired: z.coerce.boolean().default(false),
    options: z.array(picklistOptionSchema).max(100).default([]),
    defaultValue: z.unknown().optional(),
    minValue: optionalNumber(z.coerce.number()),
    maxValue: optionalNumber(z.coerce.number()),
    maxLength: optionalNumber(z.coerce.number().int().min(1).max(20_000)),
    position: z.coerce.number().int().min(0).max(200).default(0),
    isActive: z.coerce.boolean().default(true),
  })
  .refine(
    (value) => !['PICKLIST', 'MULTI_PICKLIST'].includes(value.type) || value.options.length > 0,
    // A picklist with no options is a control nobody can use.
    { message: 'Add at least one option', path: ['options'] },
  )
  .refine(
    // Null as well as undefined: a blank number input now arrives as null.
    (value) => value.minValue == null || value.maxValue == null || value.minValue <= value.maxValue,
    { message: 'Minimum must not exceed maximum', path: ['maxValue'] },
  );
export type CreateCustomFieldInput = z.infer<typeof createCustomFieldSchema>;

/**
 * `apiName`, `module` and `type` are absent on purpose: changing any of them
 * orphans every value already stored under the old key or type. Renaming the
 * *label* is free; renaming the key is a migration.
 */
export const updateCustomFieldSchema = z.object({
  label: z.string().trim().min(1).max(80).optional(),
  helpText: optionalText(200),
  isRequired: z.coerce.boolean().optional(),
  options: z.array(picklistOptionSchema).max(100).optional(),
  defaultValue: z.unknown().optional(),
  minValue: optionalNumber(z.coerce.number()),
  maxValue: optionalNumber(z.coerce.number()),
  maxLength: optionalNumber(z.coerce.number().int().min(1).max(20_000)),
  position: z.coerce.number().int().min(0).max(200).optional(),
  isActive: z.coerce.boolean().optional(),
});
export type UpdateCustomFieldInput = z.infer<typeof updateCustomFieldSchema>;

export interface CustomFieldDefinitionSummary {
  id: string;
  /** Null when the field belongs to a custom module rather than a standard one. */
  module: CrmModule | null;
  /** Set instead of `module`, for a custom module's own fields. */
  customModuleId?: string | null;
  /** For a LOOKUP: a standard module name, or `custom:<moduleId>`. */
  lookupTarget?: string | null;
  apiName: string;
  label: string;
  type: CustomFieldType;
  helpText: string | null;
  isRequired: boolean;
  options: { value: string; label: string; color?: string }[];
  defaultValue: unknown;
  minValue: number | null;
  maxValue: number | null;
  maxLength: number | null;
  position: number;
  isActive: boolean;
  /**
   * A field that is also a real column — a lead's status, a record's
   * verticals. Its options are an admin's to edit; the field itself cannot be
   * renamed, retyped, switched off or deleted.
   */
  isSystem: boolean;
  /** Options this organisation may rename but not remove, because code sets them. */
  lockedValues: string[];
}

export interface CustomFieldIssue {
  path: string;
  message: string;
}

/**
 * Validates a record's custom-field payload against the definitions.
 *
 * Hand-rolled rather than a generated Zod schema: the definitions change at
 * runtime, and building — then caching, then invalidating — a schema per
 * organization per module is more machinery than these rules justify. Returns
 * every problem at once, so a form can mark all of its bad fields in one go.
 */
export function validateCustomFields(
  definitions: readonly CustomFieldDefinitionSummary[],
  values: Record<string, unknown> | undefined,
): { values: Record<string, unknown>; issues: CustomFieldIssue[] } {
  const issues: CustomFieldIssue[] = [];
  const provided = values ?? {};
  const output: Record<string, unknown> = {};

  const active = definitions.filter((definition) => definition.isActive);
  const known = new Set(active.map((definition) => definition.apiName));

  // Values for fields that no longer exist are dropped rather than rejected: a
  // client holding a stale form should not be unable to save.
  for (const [key, value] of Object.entries(provided)) {
    if (known.has(key)) output[key] = value;
  }

  for (const definition of active) {
    const raw = output[definition.apiName];
    const isEmpty =
      raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0);

    if (isEmpty) {
      if (definition.isRequired) {
        issues.push({
          path: `customFields.${definition.apiName}`,
          message: `${definition.label} is required`,
        });
      }
      delete output[definition.apiName];
      continue;
    }

    const issue = validateOne(definition, raw);
    if (issue) {
      issues.push({ path: `customFields.${definition.apiName}`, message: issue });
      continue;
    }

    output[definition.apiName] = coerce(definition, raw);
  }

  return { values: output, issues };
}

function validateOne(definition: CustomFieldDefinitionSummary, value: unknown): string | null {
  switch (definition.type) {
    case 'NUMBER':
    case 'CURRENCY': {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return `${definition.label} must be a number`;
      if (definition.minValue !== null && parsed < definition.minValue) {
        return `${definition.label} must be at least ${definition.minValue}`;
      }
      if (definition.maxValue !== null && parsed > definition.maxValue) {
        return `${definition.label} must be at most ${definition.maxValue}`;
      }
      return null;
    }
    case 'BOOLEAN':
      return typeof value === 'boolean' ? null : `${definition.label} must be true or false`;
    case 'DATE':
    case 'DATETIME':
      return Number.isNaN(Date.parse(String(value)))
        ? `${definition.label} must be a valid date`
        : null;
    case 'EMAIL':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(String(value))
        ? null
        : `${definition.label} must be a valid email address`;
    case 'URL':
      try {
        new URL(String(value));
        return null;
      } catch {
        return `${definition.label} must be a valid URL`;
      }
    case 'PICKLIST': {
      const allowed = definition.options.map((option) => option.value);
      return allowed.includes(String(value))
        ? null
        : `${definition.label} must be one of: ${allowed.join(', ')}`;
    }
    case 'MULTI_PICKLIST': {
      if (!Array.isArray(value)) return `${definition.label} must be a list`;
      const allowed = new Set(definition.options.map((option) => option.value));
      const bad = value.filter((entry) => !allowed.has(String(entry)));
      return bad.length === 0 ? null : `${definition.label} has invalid options: ${bad.join(', ')}`;
    }
    case 'TEXT':
    case 'TEXTAREA':
    case 'PHONE': {
      const text = String(value);
      const limit = definition.maxLength ?? (definition.type === 'TEXTAREA' ? 4000 : 255);
      return text.length > limit
        ? `${definition.label} must be ${limit} characters or fewer`
        : null;
    }
    case 'LOOKUP':
      // Shape only. That the record exists, belongs to this tenant and is of
      // the right module is a database question, checked where the database
      // is — this function is pure and runs on the client too.
      return UUID.test(String(value))
        ? null
        : `${definition.label} must refer to an existing record`;
    case 'PDF': {
      // A pointer written by the upload endpoint, never typed by hand — the
      // endpoint is also what enforces "actually a PDF, under the size cap".
      const parsed = customFieldFileRefSchema.safeParse(value);
      return parsed.success ? null : `${definition.label} must be an uploaded file`;
    }
  }
}

/** A uuid, in any version. Lookups store one. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** Normalises the stored shape, so a number is a number however it arrived. */
function coerce(definition: CustomFieldDefinitionSummary, value: unknown): unknown {
  switch (definition.type) {
    case 'NUMBER':
    case 'CURRENCY':
      return Number(value);
    case 'BOOLEAN':
      return Boolean(value);
    case 'MULTI_PICKLIST':
      return Array.isArray(value) ? value.map(String) : [];
    case 'TEXT':
    case 'TEXTAREA':
    case 'EMAIL':
    case 'PHONE':
    case 'URL':
    case 'PICKLIST':
    case 'LOOKUP':
      return String(value).trim();
    default:
      // PDF: an object ref, already in its stored shape — passed through.
      return value;
  }
}

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  TEXT: 'Text',
  TEXTAREA: 'Long text',
  NUMBER: 'Number',
  CURRENCY: 'Currency',
  DATE: 'Date',
  DATETIME: 'Date and time',
  BOOLEAN: 'Checkbox',
  PICKLIST: 'Dropdown',
  MULTI_PICKLIST: 'Multi-select',
  EMAIL: 'Email',
  PHONE: 'Phone',
  URL: 'URL',
  LOOKUP: 'Lookup',
  PDF: 'PDF upload',
};

export const CUSTOM_FIELD_MODULES = CRM_MODULES;
